import { useEffect, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'

interface AnalyticsEvent {
  eventType: string
  eventData?: Record<string, any>
  pageUrl?: string
}

interface AnalyticsConfig {
  userId?: string
  sessionId: string
  enabledEvents?: string[]
}

export const useAnalytics = (config: AnalyticsConfig) => {
  const hasTrackedPageView = useRef(false)

  // Generate session ID if not provided
  const sessionId = config.sessionId || (() => {
    const existing = sessionStorage.getItem('slimehabit_session_id')
    if (existing) return existing
    
    const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem('slimehabit_session_id', newId)
    return newId
  })()

  const trackEvent = async (event: AnalyticsEvent) => {
    try {
      const { error } = await supabase.functions.invoke('analytics-tracker', {
        body: {
          userId: config.userId,
          sessionId,
          eventType: event.eventType,
          eventData: event.eventData,
          pageUrl: event.pageUrl || window.location.href,
          userAgent: navigator.userAgent
        }
      })

      if (error) {
        console.warn('Analytics tracking failed:', error)
      }
    } catch (error) {
      console.warn('Analytics tracking error:', error)
    }
  }

  const trackPageView = () => {
    if (hasTrackedPageView.current) return
    
    trackEvent({
      eventType: 'page_view',
      eventData: {
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      }
    })
    
    hasTrackedPageView.current = true
  }

  const trackUserAction = (action: string, data?: Record<string, any>) => {
    trackEvent({
      eventType: 'user_action',
      eventData: {
        action,
        ...data,
        timestamp: new Date().toISOString()
      }
    })
  }

  const trackTaskCompletion = (taskId: string, taskType: string) => {
    trackEvent({
      eventType: 'task_completed',
      eventData: {
        taskId,
        taskType,
        timestamp: new Date().toISOString()
      }
    })
  }

  const trackSlimeEvolution = (fromStage: string, toStage: string) => {
    trackEvent({
      eventType: 'slime_evolved',
      eventData: {
        fromStage,
        toStage,
        timestamp: new Date().toISOString()
      }
    })
  }

  const trackQuestCompletion = (questId: string, questType: string) => {
    trackEvent({
      eventType: 'quest_completed',
      eventData: {
        questId,
        questType,
        timestamp: new Date().toISOString()
      }
    })
  }

  // Auto-track page views
  useEffect(() => {
    trackPageView()
  }, [])

  // Track page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      trackEvent({
        eventType: document.hidden ? 'page_hidden' : 'page_visible',
        eventData: {
          timestamp: new Date().toISOString()
        }
      })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Track session duration
  useEffect(() => {
    const startTime = Date.now()
    
    const trackSessionEnd = () => {
      const duration = Date.now() - startTime
      trackEvent({
        eventType: 'session_end',
        eventData: {
          duration,
          timestamp: new Date().toISOString()
        }
      })
    }

    window.addEventListener('beforeunload', trackSessionEnd)
    return () => window.removeEventListener('beforeunload', trackSessionEnd)
  }, [])

  return {
    trackEvent,
    trackPageView,
    trackUserAction,
    trackTaskCompletion,
    trackSlimeEvolution,
    trackQuestCompletion,
    sessionId
  }
}